import { Counter } from "@/components/ui/baseUi/counter"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { useFinanceStats } from "@/modules/finances/hooks/useFinance"
import { usePeriodParam } from "@/modules/finances/hooks/usePeriod"
import { PaymentToValidateFilterStatus } from "@/modules/finances/types/financesType"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { useQueryParam } from "@modules/utils/navigation/useQueryParam"
import { SearchIcon, XIcon } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

type PaymentsToValidateTableFilterProps = {
  className?: string
} & React.ComponentProps<"div">

export function PaymentsToValidateTableFilter({ className, ...props }: PaymentsToValidateTableFilterProps) {
  const [nameFilter, setNameFilter] = useState("")

  const [period] = usePeriodParam()
  const [_nameFilterParam, setNameFilterParam] = useQueryParam("nameFilter")
  const [status, setStatus] = useQueryParam("status")
  const { data: stats } = useFinanceStats(true, period)

  useEffect(() => {
    setNameFilterParam(nameFilter)
  }, [nameFilter])

  const statusOptions = [
    { label: word("finances.page.filter.all"), value: "ALL" },
    {
      label: word("finances.page.table.status.toValidate", { count: 1 }),
      value: PaymentToValidateFilterStatus.TO_VALIDATE,
    },
    { label: word("finances.page.table.status.disputed", { count: 1 }), value: PaymentToValidateFilterStatus.DISPUTED },
    { label: word("finances.page.table.status.failed", { count: 1 }), value: PaymentToValidateFilterStatus.FAILED },
  ]

  const getTransactionTotalByStatus = useCallback(
    (filter: string): React.JSX.Element | null => {
      if (!stats?.counts) {
        return null
      }
      switch (filter) {
        case "ALL":
          return (
            <Counter variant="neutralSubtle" size="sm">
              {stats.toValidate.totalCount}
            </Counter>
          )
        case PaymentToValidateFilterStatus.TO_VALIDATE:
          return (
            <Counter variant="positiveIntense" size="sm">
              {stats.toValidate.pending.count}
            </Counter>
          )
        case PaymentToValidateFilterStatus.FAILED:
          return (
            <Counter variant="negativeIntense" size="sm">
              {stats.toValidate.failed.count}
            </Counter>
          )
        case PaymentToValidateFilterStatus.DISPUTED:
          return (
            <Counter variant="noticeIntense" size="sm">
              {stats.toValidate.disputed.count}
            </Counter>
          )
        default:
          return null
      }
    },
    [stats?.counts],
  )

  return (
    <div className={cn("flex flex-row gap-2", className)} {...props}>
      <InputGroup className="min-w-60">
        <InputGroupInput
          placeholder={word("global.search.placeholder")}
          onChange={(e) => setNameFilter(e.currentTarget.value)}
          value={nameFilter}
        />
        <InputGroupAddon align="inline-start">
          <SearchIcon color="currentColor" />
        </InputGroupAddon>
        {nameFilter && (
          <InputGroupAddon align="inline-end" onClick={() => setNameFilter("")}>
            <XIcon color="currentColor" className="cursor-pointer" />
          </InputGroupAddon>
        )}
      </InputGroup>

      <DropdownInput options={statusOptions} isRequiredInput={true}>
        <Select value={status ?? undefined} onValueChange={(value) => setStatus(value ?? "")} items={statusOptions}>
          <SelectTrigger id={"status"} className="min-w-60 bg-surface-background">
            <SelectValue placeholder={word("finances.page.table.status.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>
                <Text fontStyle="LS" className="text-textOnSurface-muted">
                  {word("finances.page.table.status.placeholder")}
                </Text>
              </SelectLabel>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex flex-row items-center gap-2">
                    {option.label}
                    {getTransactionTotalByStatus(option.value)}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </DropdownInput>
    </div>
  )
}
